import React, { useState } from "react";
import { Loader2, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { startCheckout, formatPrice } from "../lib/checkout";
import { mediaUrl } from "../lib/api";
import { SHOP_COMING_SOON } from "../lib/config";

const ProductCard = ({ product }) => {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const info = product[lang];
  const cats = t("shop.categories");
  const isSub = !!product.recurring;
  const isRevue = product.lookup_key === "revue_unit";
  const comingSoon = SHOP_COMING_SOON && !isSub;

  const PRODUCT_PAYMENT_LINKS = {
    revue_unit: "https://buy.stripe.com/fZucN45Oma4M97YdjacZa05",
  };

  const buy = async () => {
    const link = PRODUCT_PAYMENT_LINKS[product.lookup_key];
    if (link) { window.location.href = link; return; }
    setLoading(true);
    try {
      await startCheckout({ lookup_key: product.lookup_key, user_id: user && user !== false ? user.id : null });
    } catch {
      toast.error(lang === "fr" ? "Erreur lors du paiement." : "Checkout error.");
      setLoading(false);
    }
  };

  return (
    <div data-testid={`product-${product.lookup_key}`} className="group bg-card border border-border flex flex-col hover:border-primary/40 hover:-translate-y-1.5 transition-[transform,border-color] duration-500 rounded-3xl overflow-hidden">
      <div className="relative h-56 overflow-hidden">
        <img src={mediaUrl(product.image)} alt={info.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
        <span className="absolute top-4 left-4 bg-vital text-vital-foreground px-3 py-1 text-[11px] uppercase tracking-wider font-medium rounded-full">
          {cats[product.category]}
        </span>
      </div>
      <div className="p-6 lg:p-7 flex flex-col flex-1">
        <p className="text-xs text-secondary-foreground/70 mb-1">{info.format}</p>
        <h3 className="font-serif text-2xl text-foreground leading-tight mb-3">{info.name}</h3>
        <p className="text-sm text-foreground/60 font-light leading-relaxed flex-1">{info.desc}</p>
        {comingSoon ? (
          <div className="mt-6">
            <span data-testid={`comingsoon-${product.lookup_key}`} className="inline-flex items-center gap-2 border border-border text-foreground/70 px-5 py-2.5 text-sm rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-secondary" />
              {t("shop.comingSoon")}
            </span>
          </div>
        ) : (
        <div className="mt-6 flex items-center justify-between">
          <span className="font-serif text-2xl text-foreground">
            {formatPrice(product.price, product.currency)}
            {isSub && <span className="text-sm text-foreground/50"> {product.recurring === "month" ? t("shop.perMonth") : t("shop.perYear")}</span>}
          </span>
          <button
            data-testid={`buy-${product.lookup_key}`}
            onClick={buy}
            disabled={loading}
            className="group/btn flex items-center gap-1.5 bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:bg-primary/90 active:scale-95 transition-[transform,background-color] duration-200 disabled:opacity-60"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{isSub ? t("shop.subscribe") : t("shop.buy")}<ArrowUpRight className="w-4 h-4 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform" /></>}
          </button>
        </div>
        )}
      </div>
    </div>
  );
};

export default ProductCard;
